import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type Paginated } from '@/shared/api/client';
import type { FieldReport, FieldReportStatus } from '@/shared/api/types';

export interface FieldReportFilters {
  q?: string;
  status?: FieldReportStatus | '';
  projectId?: string;
  withExtraServices?: boolean;
}

export function useFieldReports(filters: FieldReportFilters) {
  const params = new URLSearchParams({ limit: '100' });
  if (filters.q) params.set('q', filters.q);
  if (filters.status) params.set('status', filters.status);
  if (filters.projectId) params.set('projectId', filters.projectId);
  if (filters.withExtraServices) params.set('withExtraServices', 'true');
  return useQuery({
    queryKey: ['field-reports', filters],
    queryFn: () => api<Paginated<FieldReport>>(`/api/field-reports?${params.toString()}`),
  });
}

export function useFieldReport(id: string | null) {
  return useQuery({
    queryKey: ['field-report', id],
    queryFn: () => api<FieldReport>(`/api/field-reports/${id}`),
    enabled: !!id,
    // Presigned URLs laufen nach 900s ab - nicht ewig aus dem Cache bedienen.
    staleTime: 10 * 60 * 1000,
  });
}

export function useReviewFieldReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      status,
      reason,
    }: {
      id: string;
      status: 'APPROVED' | 'REJECTED' | 'REVIEW_REQUIRED';
      reason?: string;
    }) =>
      api<FieldReport>(`/api/field-reports/${id}/review`, {
        method: 'PATCH',
        body: { status, ...(reason ? { reason } : {}) },
      }),
    onSuccess: (_d, { id }) => {
      qc.invalidateQueries({ queryKey: ['field-reports'] });
      qc.invalidateQueries({ queryKey: ['field-report', id] });
    },
  });
}
